import * as R from 'ramda';

import { term, terms, geo } from './constructors';
import { NonEmptyArray, NonUndefinable, Unpacked } from './types/common';
import {
  IJsonQueryType,
  TJsonQueryTermKeys,
  TJsonQueryTermsKeys,
  TJsonQueryOptionalKeys,
  IJsonQueryTerm,
  IJsonQueryTerms,
  IJsonQueryGeo,
  IJsonQuery,
  TGeoValue,
} from './types';

export function setRootType(value: IJsonQueryType): (jsonQuery: IJsonQuery) => IJsonQuery {
  return jsonQuery => ({
    ...jsonQuery,
    _type: value,
  });
}

export function setTerm<K extends TJsonQueryTermKeys>(
  key: K,
): (jsonQuery: IJsonQuery) => (value: NonUndefinable<IJsonQuery[K]>['value'] | null) => IJsonQuery {
  // @ts-expect-error Expression produces a union type that is too complex to represent
  return jsonQuery => value => R.assoc<TJsonQuery[K], TJsonQuery, K>(key, term(value), jsonQuery) as TJsonQuery;
}

export function setTerms<K extends TJsonQueryTermsKeys>(
  key: K,
): (jsonQuery: IJsonQuery) => (value: Unpacked<NonUndefinable<IJsonQuery[K]>['value']>[] | null) => IJsonQuery {
  // @ts-expect-error Expression produces a union type that is too complex to represent
  return jsonQuery => value => R.assoc<IJsonQuery[K], IJsonQuery, K>(key, terms(value), jsonQuery) as IJsonQuery;
}

export function setGeo(): (jsonQuery: IJsonQuery) => (value: TGeoValue[]) => IJsonQuery {
  return jsonQuery => value => R.assoc('geo', geo(value), jsonQuery);
}

export function resetTerms<K extends TJsonQueryOptionalKeys>(keys: K[]): (jsonQuery: IJsonQuery) => IJsonQuery {
  return jsonQuery => {
    let nextJsonQuery = R.clone(jsonQuery);

    keys.forEach(
      key => (nextJsonQuery = R.assoc<undefined, IJsonQuery, K>(key, undefined, nextJsonQuery) as IJsonQuery),
    );

    return nextJsonQuery;
  };
}

export function isTerm<T>(value: unknown): value is IJsonQueryTerm<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return typeof value === 'object' && value !== null && (value as any).type === 'term';
}

export function isTerms<T>(value: unknown): value is IJsonQueryTerms<T> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return typeof value === 'object' && value !== null && (value as any).type === 'terms';
}

export function hasTerm<K extends TJsonQueryOptionalKeys>(key: K): (jsonQuery: IJsonQuery) => boolean {
  return jsonQuery => {
    return !!jsonQuery[key];
  };
}

export function getTermValue<K extends TJsonQueryTermKeys>(
  key: K,
): (jsonQuery: IJsonQuery) => NonUndefinable<IJsonQuery[K]>['value'] | null {
  return jsonQuery => {
    const termField = jsonQuery[key];

    return isTerm<NonUndefinable<IJsonQuery[K]>['value']>(termField) ? termField.value : null;
  };
}

export function getTermsValue<K extends TJsonQueryTermsKeys>(
  key: K,
): (jsonQuery: IJsonQuery) => NonEmptyArray<Unpacked<NonUndefinable<IJsonQuery[K]>['value']>> | null {
  return jsonQuery => {
    const termsField = jsonQuery[key];

    return isTerms<NonUndefinable<IJsonQuery[K]>['value']>(termsField) && termsField.value.length > 0
      ? (termsField.value as NonEmptyArray<Unpacked<NonUndefinable<IJsonQuery[K]>['value']>>)
      : null;
  };
}

export function getGeoValue(): (jsonQuery: IJsonQuery) => IJsonQueryGeo['value'] | null {
  return jsonQuery => (jsonQuery.geo ? jsonQuery.geo.value : null);
}
